import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { DatePipe, Location } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationCancel, NavigationError, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { forkJoin, Observable, of, startWith, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ProjectService } from '../../services/project.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { ProjectIntegrationService } from '../../services/project-integration.service';
import { GithubProjectService } from '../../services/github-project.service';
import { GithubProjectConfig } from '../../models/github-project-config.model';
import { RequirementTypeService } from '../../services/requirement-type.service';
import { RequirementTypeOption } from '../../models/requirement.model';
import { TypSyncCardComponent } from '../../components/typ-sync-card/typ-sync-card.component';
import {
  KiVerbindungCardComponent,
  KiVerbindungZustandMeldung,
} from './ki-verbindung-card/ki-verbindung-card.component';
import { ProjectRoleService } from '../../services/project-role.service';
import { AccountService } from '../../account/account.service';
import { TenantUserService } from '../../services/tenant-user.service';
import { TenantUserDirectoryDTO } from '../../models/customer.model';
import { CreateProjectRequest, ProjectStatus, UpdateProjectRequest } from '../../models/project.model';
import { ProjectIntegration, ProviderType } from '../../models/project-integration.model';
import { ProjectMemberDTO } from '../../models/project-member.model';
import { ProjectRole } from '../../models/membership.model';
import { Modal } from '@spirit-testing/component-library';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { StatusPillComponent, StatusKind } from '../../components/status-pill/status-pill.component';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { createFormDirtyTracker } from '../../shared/forms/dirty-tracker';
import { providerColor, providerLabel } from '../../shared/provider-meta';
import {
  ActionsHint,
  RepositoryActionsService,
  WORKFLOWS_FOUND_KEY,
  isTokenMissingHint,
  leadingHint,
} from '../../services/repository-actions.service';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';

const MAX_CONNECTIONS = 25;
const TOKEN_MISSING_KEY = 'projectForm.integrationTokenMissingError';

export type TrackerProvider = 'JIRA' | 'REDMINE';

/**
 * Eine Verbindung zu einem Issue-Tracker. `key` ist seit NEX-969 ein rein
 * internes Kennzeichen zur Unterscheidung mehrerer Verbindungen und wird nie an
 * das Zielsystem gesendet — bei Jira heisst es "Projekt-Key", bei Redmine
 * "Kuerzel".
 */
interface TrackerConnection {
  id: string | null;
  provider: TrackerProvider;
  url: string;
  key: string;
  keyTouchedByUser?: boolean;
  syncError?: SyncError | null;
}

/** Vom letzten Speichern übrig gebliebene, ungültige Verbindung — Übersetzungsschlüssel
 * samt Platzhaltern (Provider, Host), damit Snackbar und Zeilen-Hinweis denselben Text zeigen. */
interface SyncError {
  key: string;
  params: Record<string, string>;
}

// Jira bleibt bei der Backend-Spaltenlaenge (SHORT_CODE_MAX); das Redmine-Kuerzel
// ist per Design kuerzer, weil es nur mehrere Instanzen auseinanderhalten muss.
const CONNECTION_KEY_MAX: Record<TrackerProvider, number> = {
  JIRA: FIELD_LENGTHS.SHORT_CODE_MAX,
  REDMINE: 8,
};

const CONNECTION_KEY_PLACEHOLDER: Record<TrackerProvider, string> = {
  JIRA: 'NEX',
  REDMINE: 'RED1',
};

const CONNECTION_URL_PLACEHOLDER: Record<TrackerProvider, string> = {
  JIRA: 'https://spirittesting.atlassian.net/…/boards/514',
  REDMINE: 'https://redmine.spirit-testing.com/projects/nex',
};

interface JiraBoard {
  id: string | null;
  url: string;
  key: string;
  keyTouchedByUser?: boolean;
}

const JIRA_BOARD_URL_PATTERN = /\/projects\/([a-zA-Z][a-zA-Z0-9_]+)\b/;
const JIRA_BROWSE_URL_PATTERN = /\/browse\/([a-zA-Z][a-zA-Z0-9_]+)-\d+/;

export function extractJiraProjectKey(url: string): string | null {
  if (!url) return null;
  const board = JIRA_BOARD_URL_PATTERN.exec(url);
  if (board) return board[1].toUpperCase();
  const browse = JIRA_BROWSE_URL_PATTERN.exec(url);
  if (browse) return browse[1].toUpperCase();
  return null;
}

// Rekonstruiert den JiraBoard-Formularzustand aus der persistierten Integration.
// Der Key kann aus der URL abgeleitet werden, wenn das Backend keinen speichert —
// deshalb MUSS der Diff beim Speichern gegen genau diesen abgeleiteten Zustand
// vergleichen und nicht gegen das rohe (evtl. leere) projectKey.
export function jiraBoardFromIntegration(integration: ProjectIntegration): JiraBoard {
  const url = integration.boardUrl;
  const savedKey = (integration.projectKey ?? '').toUpperCase();
  const extracted = extractJiraProjectKey(url);
  const key = savedKey || extracted || '';
  const keyTouchedByUser = !!savedKey && !!extracted && savedKey !== extracted;
  return { id: integration.id, url, key, keyTouchedByUser };
}

function connectionFromIntegration(integration: ProjectIntegration): TrackerConnection {
  if (integration.providerType === 'REDMINE') {
    return {
      id: integration.id,
      provider: 'REDMINE',
      url: integration.boardUrl,
      key: (integration.projectKey ?? '').toUpperCase(),
    };
  }
  return { ...jiraBoardFromIntegration(integration), provider: 'JIRA' };
}

interface GitHubRepo {
  id: string | null;
  url: string;
  syncError?: SyncError | null;
}

type IntegrationOpResult =
  | { ok: true }
  | { ok: false; providerType: 'GITHUB'; url: string; entry: GitHubRepo | null; error: HttpErrorResponse }
  | {
      ok: false;
      providerType: 'JIRA' | 'REDMINE';
      url: string;
      entry: TrackerConnection | null;
      error: HttpErrorResponse;
    };

interface TeamMember {
  userId: string;
  memberId: string | null;
  name: string;
  role: 'lead' | 'tester' | 'observer' | null;
}

interface ProjectIcon {
  name: string;
  matIcon: string;
  color: string;
  labelKey: string;
}

const PROJECT_ICONS: ProjectIcon[] = [
  { name: 'folder', matIcon: 'folder', color: 'var(--to-project-icon-folder)', labelKey: 'projectForm.icons.folder' },
  {
    name: 'check',
    matIcon: 'check_circle',
    color: 'var(--to-project-icon-check)',
    labelKey: 'projectForm.icons.check',
  },
  { name: 'chart', matIcon: 'bar_chart', color: 'var(--to-project-icon-chart)', labelKey: 'projectForm.icons.chart' },
  { name: 'users', matIcon: 'group', color: 'var(--to-project-icon-users)', labelKey: 'projectForm.icons.users' },
  { name: 'shield', matIcon: 'shield', color: 'var(--to-project-icon-shield)', labelKey: 'projectForm.icons.shield' },
  {
    name: 'palette',
    matIcon: 'palette',
    color: 'var(--to-project-icon-palette)',
    labelKey: 'projectForm.icons.palette',
  },
  {
    name: 'settings',
    matIcon: 'settings',
    color: 'var(--to-project-icon-settings)',
    labelKey: 'projectForm.icons.settings',
  },
  { name: 'key', matIcon: 'key', color: 'var(--to-project-icon-key)', labelKey: 'projectForm.icons.key' },
];

const STATUS_PILL_MAP: Record<ProjectStatus, StatusKind> = {
  entwurf: 'inaktiv',
  aktiv: 'aktiv',
  pausiert: 'pending',
  archiviert: 'inaktiv',
};

@Component({
  selector: 'app-project-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    DatePipe,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatProgressSpinner,
    Modal,
    AvatarComponent,
    StatusPillComponent,
    MatButton,
    TypSyncCardComponent,
    KiVerbindungCardComponent,
  ],
  templateUrl: './project-form.component.html',
  styleUrl: './project-form.component.scss',
})
export class ProjectFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private projectService = inject(ProjectService);
  private projectMemberService = inject(ProjectMemberService);
  private projectIntegrationService = inject(ProjectIntegrationService);
  private githubProjectService = inject(GithubProjectService);
  private repositoryActionsService = inject(RepositoryActionsService);
  private requirementTypeService = inject(RequirementTypeService);
  private projectRoleService = inject(ProjectRoleService, { optional: true });
  private accountService = inject(AccountService);
  private tenantUserService = inject(TenantUserService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  readonly icons = PROJECT_ICONS;
  readonly statusOptions: { value: ProjectStatus; labelKey: string }[] = [
    { value: 'entwurf', labelKey: 'projectForm.status.entwurf' },
    { value: 'aktiv', labelKey: 'projectForm.status.aktiv' },
    { value: 'pausiert', labelKey: 'projectForm.status.pausiert' },
    { value: 'archiviert', labelKey: 'projectForm.status.archiviert' },
  ];
  readonly teamRoles: { value: TeamMember['role']; labelKey: string }[] = [
    { value: 'lead', labelKey: 'projectForm.team.roles.lead' },
    { value: 'tester', labelKey: 'projectForm.team.roles.tester' },
    { value: 'observer', labelKey: 'projectForm.team.roles.observer' },
  ];

  readonly projectId = signal<string | null>(null);
  readonly isEditMode = computed(() => !!this.projectId());
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly availableUsers = signal<TenantUserDirectoryDTO[]>([]);
  readonly team = signal<TeamMember[]>([]);
  readonly originalMemberDtos = signal<ProjectMemberDTO[]>([]);
  readonly maxConnections = MAX_CONNECTIONS;
  readonly trackerOptions: TrackerProvider[] = ['JIRA', 'REDMINE'];
  readonly providerLabel = providerLabel;
  readonly providerColor = providerColor;
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();
  readonly existingIntegrations = signal<ProjectIntegration[]>([]);
  readonly existingRepositories = signal<GithubProjectConfig[]>([]);
  readonly requirementTypes = signal<RequirementTypeOption[]>([]);
  /**
   * NEX-1077 Review: keine eigene Rollen-Ableitung mehr — `ProjectRoleService`
   * ist auf der Route bereitgestellt und per Resolver bereits geladen. Der
   * Service kennt zusaetzlich `isPlatformAdmin`, den unser alter Guard verpasst
   * hatte (Plattform-Admin ohne Projekt-Mitgliedschaft sah die Karte nie).
   */
  readonly canManageRequirementTypes = computed(() => {
    if (this.projectRoleService?.isPlatformAdmin()) return true;
    const role = this.projectRoleService?.role();
    return role === 'PROJECT_ADMIN' || role === 'PROJECT_WRITER';
  });
  private readonly profileRole = signal<string | null>(null);
  private readonly istMandantenAdmin = computed(
    () => this.profileRole() === 'TENANT_ADMIN' || this.profileRole() === 'PLATFORM_ADMIN',
  );
  // Lesen dürfen auch Writer (nur lesbar); Reader sehen die Karte nicht (NEX-1562 AK11).
  readonly kiSichtbar = computed(
    () => this.istMandantenAdmin() || this.projectRoleService?.role() !== 'PROJECT_READER',
  );
  readonly kiDarfBearbeiten = computed(
    () => this.istMandantenAdmin() || this.projectRoleService?.role() === 'PROJECT_ADMIN',
  );
  private readonly kiKarte = viewChild(KiVerbindungCardComponent);
  // Gespiegelt statt über kiKarte() gelesen: ein Query-Signal in isDirty/saveDisabled hielt die
  // Change Detection in einer Endlosschleife.
  readonly kiZustand = signal<KiVerbindungZustandMeldung>({ dirty: false, valid: true });
  // Eine gemeinsame Liste fuer Jira- und Redmine-Verbindungen: der Provider ist
  // eine Eigenschaft der Zeile und per Umschalter wechselbar (NEX-971).
  readonly connections = signal<TrackerConnection[]>([]);
  readonly jiraBoards = computed<TrackerConnection[]>(() => this.connections().filter((c) => c.provider === 'JIRA'));
  readonly redmineBoards = computed<TrackerConnection[]>(() =>
    this.connections().filter((c) => c.provider === 'REDMINE'),
  );
  readonly githubRepos = signal<GitHubRepo[]>([]);
  readonly memberSearch = signal('');
  readonly memberSearchOpen = signal(false);
  readonly fieldLengths = FIELD_LENGTHS;
  readonly form = new FormGroup({
    name: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)],
      nonNullable: true,
    }),
    description: new FormControl('', {
      validators: [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)],
      nonNullable: true,
    }),
    icon: new FormControl('folder', {
      validators: [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)],
      nonNullable: true,
    }),
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null),
    status: new FormControl<ProjectStatus | null>(null, [Validators.required]),
    tags: new FormControl('', { validators: [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)], nonNullable: true }),
  });

  private readonly formValues = toSignal(this.form.valueChanges.pipe(startWith(this.form.value)), {
    initialValue: this.form.value,
  });

  // Team-Liste und Integrations-Arrays leben außerhalb des ReactiveForm; sie
  // fließen als Extra-State in den Dirty-Vergleich gegen den Original-Snapshot.
  private readonly dirtyTracker = createFormDirtyTracker(this.form, {
    extra: () => ({
      team: this.team(),
      connections: this.connections(),
      githubRepos: this.githubRepos(),
    }),
  });
  readonly isDirty = computed(() => this.dirtyTracker.isDirty() || this.kiZustand().dirty);

  private readonly formValid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((s) => s === 'VALID'),
    ),
    { initialValue: false },
  );

  readonly previewName = computed(() => this.formValues()?.name ?? '');
  readonly previewIcon = computed(
    () => PROJECT_ICONS.find((i) => i.name === (this.formValues()?.icon ?? 'folder')) ?? PROJECT_ICONS[0],
  );
  readonly previewStatus = computed(() => this.formValues()?.status ?? null);
  readonly previewStatusKind = computed(() =>
    this.previewStatus() ? STATUS_PILL_MAP[this.previewStatus()!] : 'inaktiv',
  );
  readonly previewStatusLabel = computed(() =>
    this.previewStatus() ? this.translate.instant(`projectForm.status.${this.previewStatus()}`) : '',
  );
  readonly previewJiraKeys = computed(() =>
    this.jiraBoards()
      .map((b) => b.key.trim())
      .filter(Boolean)
      .join(', '),
  );
  readonly leadMembers = computed(() => this.team().filter((m) => m.role === 'lead'));

  readonly hasLead = computed(() => this.leadMembers().length > 0);

  readonly leadMember = computed(() => this.leadMembers()[0] ?? null);

  readonly previewResponsibleNames = computed(() => this.leadMembers().map((m) => m.name));

  readonly previewResponsibleLabelKey = computed(() =>
    this.leadMembers().length > 1 ? 'projectForm.team.responsiblePlural' : 'projectForm.team.responsible',
  );

  readonly previewResponsibleName = computed(() => this.previewResponsibleNames().join(', '));

  readonly hasUnassignedRole = computed(() => this.team().some((m) => m.role === null));

  readonly teamAlertKey = computed<string | null>(() => (!this.hasLead() ? 'projectForm.errors.noLead' : null));

  readonly durationDays = computed(() => {
    const start = this.formValues()?.startDate;
    const end = this.formValues()?.endDate;
    if (!(start instanceof Date) || !(end instanceof Date)) return null;
    const diff = end.getTime() - start.getTime();
    return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
  });

  readonly filteredUsers = computed(() => {
    const q = this.memberSearch().toLowerCase();
    const teamIds = new Set(this.team().map((m) => m.userId));
    return this.availableUsers()
      .filter((u) => !teamIds.has(u.id))
      .filter((u) => !q || u.displayName.toLowerCase().includes(q));
  });

  readonly saveDisabled = computed(
    () =>
      this.isSaving() ||
      !this.formValid() ||
      !this.hasLead() ||
      this.hasUnassignedRole() ||
      !this.kiZustand().valid ||
      (this.isEditMode() && !this.isDirty()),
  );

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('projectId');
    this.projectId.set(id);

    if (id) {
      this.isLoading.set(true);
      this.accountService
        .getProfile()
        .pipe(
          tap((profile) => this.profileRole.set(profile.role)),
          switchMap((profile) =>
            forkJoin({
              users: this.tenantUserService.getUsersDirectory(profile.customerId),
              project: this.projectService.getById(id),
              members: this.projectMemberService.getMembers(id),
              integrations: this.projectIntegrationService.list(id),
              repositories: this.githubProjectService.list(id),
              // NEX-1077 Review: catchError, damit ein 500 auf `requirement-types` waehrend
              // des Rollouts nicht die gesamte Projekt-Bearbeitung sprengt und den Nutzer
              // aufs Dashboard wirft. Ohne diesen Fallback bricht `forkJoin` komplett ab.
              requirementTypes: this.requirementTypeService
                .list(id)
                .pipe(catchError(() => of<RequirementTypeOption[]>([]))),
            }),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: ({ users, project, members, integrations, repositories, requirementTypes }) => {
            this.existingIntegrations.set(integrations);
            this.requirementTypes.set(requirementTypes);
            this.connections.set(
              integrations.filter((i) => i.providerType !== 'GITHUB').map(connectionFromIntegration),
            );
            this.applyRepositories(repositories);
            this.availableUsers.set(users);
            this.originalMemberDtos.set(members);
            this.team.set(
              members
                .filter((d) => d.active && d.tenantUserId)
                .map((d) => ({
                  memberId: d.id,
                  userId: d.tenantUserId!,
                  name: [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || d.email,
                  role: this.fromBackendRole(d.roles),
                })),
            );
            this.form.patchValue({
              name: project.name,
              startDate: project.startDate ? this.parseDate(project.startDate) : null,
              endDate: project.endDate ? this.parseDate(project.endDate) : null,
              description: project.description ?? '',
              icon: project.icon ?? 'folder',
              tags: project.tags ?? '',
              status: project.status ?? 'aktiv',
            });
            this.dirtyTracker.reset();
            this.isLoading.set(false);
          },
          error: () => {
            this.isLoading.set(false);
            this.snackBar.open(this.translate.instant('projectForm.errors.loadError'), undefined, {
              panelClass: 'snackbar-error',
            });
            this.router.navigate(['/dashboard']);
          },
        });
    } else {
      this.accountService
        .getProfile()
        .pipe(
          switchMap((profile) => this.tenantUserService.getUsersDirectory(profile.customerId)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((users) => {
          this.availableUsers.set(users);
        });
    }
  }

  selectIcon(name: string): void {
    this.form.controls.icon.setValue(name);
  }

  addTeamMember(user: TenantUserDirectoryDTO): void {
    this.team.update((t) => [...t, { memberId: null, userId: user.id, name: user.displayName, role: null }]);
    this.memberSearch.set('');
    this.memberSearchOpen.set(false);
  }

  openMemberSearch(): void {
    this.memberSearchOpen.set(true);
  }

  onMemberAddFocusOut(event: FocusEvent): void {
    const wrapper = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (next && wrapper.contains(next)) {
      return;
    }
    this.memberSearchOpen.set(false);
  }

  removeTeamMember(userId: string): void {
    this.team.update((t) => t.filter((m) => m.userId !== userId));
  }

  updateTeamRole(userId: string, role: TeamMember['role']): void {
    this.team.update((t) => t.map((m) => (m.userId === userId ? { ...m, role } : m)));
  }

  onSave(): void {
    if (this.form.invalid || !this.leadMember() || this.hasUnassignedRole() || !this.kiZustand().valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);

    const v = this.form.getRawValue();
    const id = this.projectId();

    if (id) {
      const request: UpdateProjectRequest = {
        name: v.name,
        startDate: v.startDate ? this.toDateStr(v.startDate) : undefined,
        endDate: v.endDate ? this.toDateStr(v.endDate) : null,
        description: v.description || undefined,
        icon: v.icon || undefined,
        tags: v.tags || undefined,
        status: v.status!,
      };
      this.projectService
        .update(id, request)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.saveKiVerbindungThenSync(id),
          error: () => {
            this.isSaving.set(false);
            this.snackBar.open(this.translate.instant('projectForm.errors.saveError'), undefined, {
              politeness: 'assertive',
              panelClass: 'snackbar-error',
            });
          },
        });
    } else {
      const leadUserId = this.leadMember()!.userId;
      const request: CreateProjectRequest = {
        name: v.name,
        startDate: v.startDate ? this.toDateStr(v.startDate) : '',
        leadUserId,
        description: v.description || undefined,
        icon: v.icon || undefined,
        tags: v.tags || undefined,
        status: v.status!,
        ...(v.endDate ? { endDate: this.toDateStr(v.endDate) } : {}),
      };
      this.projectService
        .create(request)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (project) => {
            const memberOps = this.team()
              .filter((m) => m.userId !== leadUserId)
              .map((m) =>
                this.projectMemberService.addMember(project.id, {
                  tenantUserId: m.userId,
                  roles: this.toBackendRoles(m.role),
                  active: true,
                }),
              );
            const githubOps = this.buildRepositoryOps(project.id);
            const integrationOps = [
              ...this.buildIntegrationOps(project.id, this.jiraBoards(), 'JIRA'),
              ...githubOps,
              ...this.buildIntegrationOps(project.id, this.redmineBoards(), 'REDMINE'),
            ];
            if (memberOps.length === 0 && integrationOps.length === 0) {
              this.finishCreateAndNavigate(null);
              return;
            }
            forkJoin({
              members: this.runBucket(memberOps),
              integrations: this.runIntegrationOps(integrationOps),
            })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(({ members, integrations }) => {
                const integrationFailures = this.failedIntegrations(integrations);
                if (members.ok && integrationFailures.length === 0) {
                  if (githubOps.length === 0) {
                    this.finishCreateAndNavigate(null);
                    return;
                  }
                  this.finishAfterRepositoryCheck(project.id, (hint) => this.finishCreateAndNavigate(hint));
                  return;
                }
                this.isSaving.set(false);
                if (!members.ok) {
                  this.snackBar.open(this.translate.instant('projectForm.memberSyncError'), undefined, {
                    duration: 6000,
                    politeness: 'assertive',
                    panelClass: 'snackbar-error',
                  });
                }
                if (integrationFailures.length === 0) {
                  this.router.navigate(['/dashboard']);
                  return;
                }
                // Projekt existiert bereits serverseitig — ab hier auf Update umschalten,
                // sonst legt ein erneutes Speichern ein zweites Projekt an.
                this.projectId.set(project.id);
                this.showEditAddress(project.id);
                this.reportIntegrationFailures(project.id, integrationFailures);
              });
          },
          error: () => {
            this.isSaving.set(false);
            this.snackBar.open(this.translate.instant('projectForm.errors.saveError'), undefined, {
              politeness: 'assertive',
              panelClass: 'snackbar-error',
            });
          },
        });
    }
  }

  onCancel(): void {
    this.router.navigate(['/dashboard']);
  }

  hasUnsavedChanges(): boolean {
    return this.isDirty();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  stripTrailingSlash(url: string): string {
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  connectionKeyMax(provider: TrackerProvider): number {
    return CONNECTION_KEY_MAX[provider];
  }

  connectionKeyLabelKey(provider: TrackerProvider): string {
    return `projectForm.integration.keyLabel.${provider.toLowerCase()}`;
  }

  connectionKeyPlaceholder(provider: TrackerProvider): string {
    return CONNECTION_KEY_PLACEHOLDER[provider];
  }

  connectionUrlPlaceholder(provider: TrackerProvider): string {
    return CONNECTION_URL_PLACEHOLDER[provider];
  }

  /**
   * Redmine zeigt bewusst die Instanz-Issues-Liste ohne `project_id`: das
   * Kennzeichen ist kein Projektbezug mehr, und das Backend liest seit NEX-970
   * ebenfalls ueber die gesamte Instanz.
   */
  connectionPreviewUrl(connection: TrackerConnection): string {
    const url = this.stripTrailingSlash(connection.url.trim());
    if (connection.provider === 'REDMINE') {
      return `${this.baseUrlOf(url)}/issues`;
    }
    return `${url}/browse/${connection.key}-123`;
  }

  onRequirementTypesChanged(): void {
    const id = this.projectId();
    if (!id) return;
    this.requirementTypeService
      .list(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) => this.requirementTypes.set(types),
      });
  }

  private baseUrlOf(url: string): string {
    try {
      return new URL(url).origin;
    } catch {
      return url;
    }
  }

  addConnection(provider: TrackerProvider = 'JIRA'): void {
    this.addEntry(this.connections, { id: null, provider, url: '', key: '' });
  }

  removeConnection(index: number): void {
    this.removeEntry(this.connections, index);
  }

  updateConnectionUrl(index: number, url: string): void {
    this.updateEntry(this.connections, index, (c) => {
      if (c.provider !== 'JIRA') return { ...c, url, syncError: null };
      const extracted = extractJiraProjectKey(url);
      if (extracted !== null && !c.keyTouchedByUser) {
        return { ...c, url, key: extracted, syncError: null };
      }
      return { ...c, url, syncError: null };
    });
  }

  updateConnectionKey(index: number, key: string): void {
    this.updateEntry(this.connections, index, (c) => ({
      ...c,
      key: this.normalizeKey(key, c.provider),
      keyTouchedByUser: true,
      syncError: null,
    }));
  }

  /**
   * Der Provider einer bereits gespeicherten Verbindung laesst sich nicht
   * umschreiben — der Update-Endpunkt kennt kein `providerType`. Die Zeile
   * verliert deshalb ihre id und wird beim Speichern als Loeschen + Neuanlage
   * abgewickelt.
   */
  setConnectionProvider(index: number, provider: TrackerProvider): void {
    this.updateEntry(this.connections, index, (c) =>
      c.provider === provider
        ? c
        : { ...c, id: null, provider, key: this.normalizeKey(c.key, provider), syncError: null },
    );
  }

  private normalizeKey(key: string, provider: TrackerProvider): string {
    return key.toUpperCase().slice(0, CONNECTION_KEY_MAX[provider]);
  }

  addGithubRepo(): void {
    this.addEntry(this.githubRepos, { id: null, url: '' });
  }

  removeGithubRepo(index: number): void {
    this.removeEntry(this.githubRepos, index);
  }

  updateGithubRepoUrl(index: number, url: string): void {
    this.updateEntry(this.githubRepos, index, (r) => ({ ...r, url, syncError: null }));
  }

  private addEntry<T>(sig: WritableSignal<T[]>, blank: T): void {
    if (sig().length >= MAX_CONNECTIONS) return;
    sig.update((list) => [...list, blank]);
  }

  private removeEntry<T>(sig: WritableSignal<T[]>, index: number): void {
    sig.update((list) => list.filter((_, i) => i !== index));
  }

  private updateEntry<T>(sig: WritableSignal<T[]>, index: number, updater: (item: T) => T): void {
    sig.update((list) => list.map((item, i) => (i === index ? updater(item) : item)));
  }

  /** Scheitert die KI-Verbindung, bleibt das Formular offen: der Fehler steht in der Karte. */
  private saveKiVerbindungThenSync(projectId: string): void {
    const karte = this.kiKarte();
    if (!karte) {
      this.syncMembersAndIntegrations(projectId);
      return;
    }
    karte
      .speichern()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ok) => {
        if (!ok) {
          this.isSaving.set(false);
          return;
        }
        // Die Meldung der Karte kommt erst mit dem nächsten Change-Detection-Lauf; ohne sofortiges
        // Nachziehen sähe der Guard beim Wegnavigieren noch den alten Dirty-Stand.
        this.kiZustand.set({ dirty: karte.isDirty(), valid: karte.isValid() });
        this.syncMembersAndIntegrations(projectId);
      });
  }

  private syncMembersAndIntegrations(projectId: string): void {
    const memberOps = this.buildMemberOps(projectId);
    const githubOps = this.buildRepositoryOps(projectId);
    const integrationOps = [
      ...this.buildIntegrationOps(projectId, this.jiraBoards(), 'JIRA'),
      ...githubOps,
      ...this.buildIntegrationOps(projectId, this.redmineBoards(), 'REDMINE'),
    ];

    if (memberOps.length === 0 && integrationOps.length === 0) {
      this.finishSaveSuccess(null);
      return;
    }

    forkJoin({
      members: this.runBucket(memberOps),
      integrations: this.runIntegrationOps(integrationOps),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ members, integrations }) => {
        const integrationFailures = this.failedIntegrations(integrations);
        if (members.ok && integrationFailures.length === 0) {
          if (githubOps.length === 0) {
            this.finishSaveSuccess(null);
            return;
          }
          this.finishAfterRepositoryCheck(projectId, (hint) => this.finishSaveSuccess(hint));
          return;
        }
        this.handleBucketErrors(projectId, members, integrationFailures);
      });
  }

  private runIntegrationOps(ops: Observable<IntegrationOpResult>[]): Observable<IntegrationOpResult[]> {
    return ops.length ? forkJoin(ops) : of([]);
  }

  private failedIntegrations(results: IntegrationOpResult[]): Extract<IntegrationOpResult, { ok: false }>[] {
    return results.filter((r): r is Extract<IntegrationOpResult, { ok: false }> => !r.ok);
  }

  private buildMemberOps(projectId: string): Observable<unknown>[] {
    const current = this.team();
    const original = this.originalMemberDtos();

    const memberAdds = current
      .filter((m) => !m.memberId)
      .map((m) =>
        this.projectMemberService.addMember(projectId, {
          tenantUserId: m.userId,
          roles: this.toBackendRoles(m.role),
          active: true,
        }),
      );
    const memberRemoves = original
      .filter((o) => !current.find((c) => c.memberId === o.id))
      .map((o) => this.projectMemberService.deleteMember(projectId, o.id));
    const memberUpdates = current
      .filter((m) => {
        if (!m.memberId) return false;
        const orig = original.find((o) => o.id === m.memberId);
        return orig && this.toBackendRoles(m.role)[0] !== orig.roles[0];
      })
      .map((m) =>
        this.projectMemberService.updateMember(projectId, m.memberId!, { roles: this.toBackendRoles(m.role) }),
      );
    return [...memberAdds, ...memberRemoves, ...memberUpdates];
  }

  private runBucket(ops: Observable<unknown>[]): Observable<{ ok: true } | { ok: false; error: HttpErrorResponse }> {
    if (ops.length === 0) return of({ ok: true as const });
    return forkJoin(ops).pipe(
      map(() => ({ ok: true as const })),
      catchError((error: HttpErrorResponse) => of({ ok: false as const, error })),
    );
  }

  private finishCreateAndNavigate(hint: ActionsHint | null): void {
    this.isSaving.set(false);
    this.dirtyTracker.reset();
    this.router.navigate(['/dashboard']);
    if (hint) {
      this.openActionsHint(hint);
    }
  }

  private finishSaveSuccess(hint: ActionsHint | null): void {
    this.isSaving.set(false);
    this.dirtyTracker.reset();
    this.router.navigate(['/dashboard']);
    if (hint) {
      this.openActionsHint(hint);
      return;
    }
    this.snackBar.open(this.translate.instant('projectForm.saveSuccess'), undefined, {
      panelClass: 'snackbar-success',
    });
  }

  /**
   * Der Abschluss nach einem Speichern, das eine Repository-URL geschrieben hat: erst die
   * Rueckmeldung zum Repository holen, dann abschliessen (NEX-1292). Wer nichts geschrieben
   * hat, ruft {@code finish} unmittelbar auf — die Antwort kostet einen Aufruf beim
   * Anbieter je Repository, und ein reines Speichern der Mitglieder hat zu den Workflows
   * nichts Neues zu sagen.
   *
   * <p>Berichtet wird allein ueber die Snackbar: beide Abschluesse verlassen das Formular
   * zum Dashboard, und ein Hinweis in der Zeile des Repositories waere mit ihm zerstoert,
   * bevor ihn jemand liest.
   */
  private finishAfterRepositoryCheck(projectId: string, finish: (hint: ActionsHint | null) => void): void {
    this.repositoryActionsService
      .probe(projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((actions) => finish(leadingHint(actions)));
  }

  private openActionsHint(hint: ActionsHint): void {
    this.openTokenAwareSnackBar(this.translate.instant(hint.key, hint.params), isTokenMissingHint(hint), {
      duration: 6000,
      panelClass: hint.key === WORKFLOWS_FOUND_KEY ? 'snackbar-success' : 'snackbar-error',
    });
  }

  /**
   * Eine Meldung, die bei fehlendem Token weiter zur Konto-Seite fuehrt: dort wird es
   * hinterlegt, und ohne diesen Weg bleibt die Meldung eine Sackgasse.
   */
  private openTokenAwareSnackBar(message: string, tokenMissing: boolean, config: MatSnackBarConfig): void {
    const ref = this.snackBar.open(
      message,
      tokenMissing ? this.translate.instant('projectForm.integration.goToAccount') : undefined,
      config,
    );
    if (tokenMissing) {
      ref
        .onAction()
        .pipe(take(1))
        .subscribe(() => this.router.navigate(['/account']));
    }
  }

  private handleBucketErrors(
    projectId: string,
    members: { ok: true } | { ok: false; error: HttpErrorResponse },
    integrationFailures: Extract<IntegrationOpResult, { ok: false }>[],
  ): void {
    this.isSaving.set(false);
    if (!members.ok) {
      this.snackBar.open(this.translate.instant('projectForm.memberSyncError'), undefined, {
        duration: 6000,
        politeness: 'assertive',
        panelClass: 'snackbar-error',
      });
    }
    this.reportIntegrationFailures(projectId, integrationFailures);
  }

  /**
   * Adresse ohne Navigation auf die Bearbeiten-Route setzen: Eine Navigation baute die Komponente
   * neu auf und verwürfe die abgelehnte, noch ungespeicherte Verbindung. Der Router kennt die neue
   * Adresse nicht und setzt sie nach einer abgebrochenen Navigation (Verlassen-Dialog „Bleiben“)
   * auf `/projects/new` zurück — deshalb danach erneut setzen.
   */
  private showEditAddress(projectId: string): void {
    const editPath = `/projects/${projectId}/edit`;
    this.location.replaceState(editPath);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationCancel || event instanceof NavigationError),
        filter(() => this.location.path() !== editPath),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.location.replaceState(editPath));
  }

  /** Zeigt eine providerbezogene Meldung je fehlgeschlagener Integration und lädt den
   * Projektzustand neu — die abgelehnte(n) Zeile(n) werden danach zurückgemischt. */
  private reportIntegrationFailures(projectId: string, failures: Extract<IntegrationOpResult, { ok: false }>[]): void {
    const messages = failures.map((failure) => ({
      failure,
      syncError: this.integrationErrorMessage(failure.providerType, failure.url, failure.error),
    }));
    for (const { syncError } of messages) {
      this.openTokenAwareSnackBar(
        this.translate.instant(syncError.key, syncError.params),
        syncError.key === TOKEN_MISSING_KEY,
        { duration: 6000, politeness: 'assertive', panelClass: 'snackbar-error' },
      );
    }
    this.reloadProjectState(projectId, () => this.applyIntegrationFailures(messages));
  }

  /** Ordnet den Backend-Fehlercode (`*_TOKEN_MISSING`) einer providerneutralen Meldung
   * zu — Provider und Host kommen aus dem Aufruf, nicht aus fest verdrahtetem Text. */
  private integrationErrorMessage(providerType: ProviderType, url: string, error: HttpErrorResponse): SyncError {
    const provider = providerLabel(providerType);
    const code = this.errorCode(error);
    if (code.endsWith('_TOKEN_MISSING')) {
      return { key: TOKEN_MISSING_KEY, params: { provider, host: this.hostOf(url) } };
    }
    if (error.status >= 400 && error.status < 500) {
      return { key: 'projectForm.integrationSyncValidationError', params: { provider } };
    }
    return { key: 'projectForm.integrationSyncError', params: { provider } };
  }

  private errorCode(error: HttpErrorResponse): string {
    const body = error.error as { code?: string } | null;
    return body?.code ?? '';
  }

  private hostOf(url: string): string {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }

  /** Hält die abgelehnte(n) Verbindungszeile(n) im Formular statt sie zu verwerfen — der
   * Server-Reload davor überschreibt Connections/Repos bereits mit dem persistierten Stand. */
  private applyIntegrationFailures(
    messages: { failure: Extract<IntegrationOpResult, { ok: false }>; syncError: SyncError }[],
  ): void {
    for (const { failure, syncError } of messages) {
      if (!failure.entry) continue;
      if (failure.providerType === 'GITHUB') {
        const rejected: GitHubRepo = { ...failure.entry, syncError };
        this.githubRepos.update((list) => this.replaceOrAppend(list, rejected));
      } else {
        const rejected: TrackerConnection = { ...failure.entry, syncError };
        this.connections.update((list) => this.replaceOrAppend(list, rejected));
      }
    }
  }

  /** Ersetzt eine bereits geladene Verbindung an ihrer Stelle (per `id`), statt sie ein
   * zweites Mal anzuhängen — der Reload davor hat den vorherigen (jetzt veralteten)
   * Stand genau dieser Integration bereits geladen. Nur eine neue Verbindung (`id: null`)
   * wird angehängt, weil sie im Reload naturgemäß nicht vorkommen kann. */
  private replaceOrAppend<T extends { id: string | null }>(list: T[], entry: T): T[] {
    if (entry.id === null) return [...list, entry];
    const index = list.findIndex((item) => item.id === entry.id);
    return index === -1 ? [...list, entry] : list.map((item, i) => (i === index ? entry : item));
  }

  private reloadProjectState(projectId: string, onComplete?: () => void): void {
    forkJoin({
      members: this.projectMemberService.getMembers(projectId),
      integrations: this.projectIntegrationService.list(projectId),
      repositories: this.githubProjectService.list(projectId),
      // NEX-1077 Review: gleiche Fehlerbehandlung wie im initialen forkJoin,
      // sonst bricht der Reload nach dem Save durch einen transienten 500 ab.
      requirementTypes: this.requirementTypeService
        .list(projectId)
        .pipe(catchError(() => of<RequirementTypeOption[]>([]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ members, integrations, repositories, requirementTypes }) => {
          this.originalMemberDtos.set(members);
          this.existingIntegrations.set(integrations);
          this.requirementTypes.set(requirementTypes);
          this.connections.set(integrations.filter((i) => i.providerType !== 'GITHUB').map(connectionFromIntegration));
          this.applyRepositories(repositories);
          this.team.set(
            members
              .filter((d) => d.active && d.tenantUserId)
              .map((d) => ({
                memberId: d.id,
                userId: d.tenantUserId!,
                name: [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || d.email,
                role: this.fromBackendRole(d.roles),
              })),
          );
          this.dirtyTracker.reset();
          onComplete?.();
        },
        error: () => {
          // Reload selbst gescheitert — Snackbar reicht, UI bleibt so wie sie ist.
        },
      });
  }

  private applyRepositories(repositories: GithubProjectConfig[]): void {
    this.existingRepositories.set(repositories);
    this.githubRepos.set(repositories.map((repository) => ({ id: repository.id, url: repository.repoUrl })));
  }

  private buildRepositoryOps(projectId: string): Observable<IntegrationOpResult>[] {
    const filled = this.githubRepos().filter((repo) => repo.url.trim());
    const bound = this.existingRepositories();

    const wrap = (op: Observable<unknown>, url: string, entry: GitHubRepo | null): Observable<IntegrationOpResult> =>
      op.pipe(
        map(() => ({ ok: true as const })),
        catchError((error: HttpErrorResponse) =>
          of({ ok: false as const, providerType: 'GITHUB' as const, url, entry, error }),
        ),
      );

    const upserts: Observable<IntegrationOpResult>[] = filled.flatMap((repo) => {
      const url = repo.url.trim();
      if (!repo.id) {
        return [wrap(this.githubProjectService.create(projectId, url), url, repo)];
      }
      const original = bound.find((binding) => binding.id === repo.id);
      // Ohne Token hat die Bindung keinen auflösbaren Eigentümer (NEX-1318-Altfall). Der PATCH
      // ist dann der einzige Weg, sie zu übernehmen — sonst bleibt sie dauerhaft stumm.
      if (
        original &&
        original.tokenAvailable &&
        this.stripTrailingSlash(url) === this.stripTrailingSlash(original.repoUrl.trim())
      ) {
        return [];
      }
      return [wrap(this.githubProjectService.update(projectId, repo.id, url), url, repo)];
    });

    const deletes: Observable<IntegrationOpResult>[] = bound
      .filter((binding) => !filled.find((repo) => repo.id === binding.id))
      .map((binding) => wrap(this.githubProjectService.delete(projectId, binding.id), binding.repoUrl, null));

    return [...upserts, ...deletes];
  }

  private buildIntegrationOps(
    projectId: string,
    entries: (TrackerConnection | GitHubRepo)[],
    providerType: ProviderType,
  ): Observable<IntegrationOpResult>[] {
    const filled = entries.filter((e) => e.url.trim());
    const existing = this.existingIntegrations().filter((i) => i.providerType === providerType);

    // Der Provider ist je Aufruf fix (eine der drei Listen), aber innerhalb dieser
    // einen Methode fuer alle drei gemeinsam generisch — TS kann die Korrelation
    // zwischen `providerType` und dem konkreten `entry`-Typ hier nicht selbst herleiten.
    const wrap = (
      op: Observable<unknown>,
      url: string,
      entry: TrackerConnection | GitHubRepo | null,
    ): Observable<IntegrationOpResult> =>
      op.pipe(
        map(() => ({ ok: true as const })),
        catchError((error: HttpErrorResponse) =>
          of({ ok: false as const, providerType, url, entry, error } as IntegrationOpResult),
        ),
      );

    // Nur echte Änderungen senden (analog buildMemberOps): eine unveränderte
    // Integration erneut zu validieren löste sonst bei bereits fehlerhaften Daten
    // einen irreführenden "Integration abgelehnt"-Snackbar beim reinen Member-Save aus (NEX-605).
    const upserts: Observable<IntegrationOpResult>[] = filled.flatMap((e) => {
      const key = 'key' in e ? e.key : undefined;
      if (!e.id) {
        return [
          wrap(
            this.projectIntegrationService.create(projectId, {
              providerType,
              boardUrl: e.url.trim(),
              ...(key ? { projectKey: key } : {}),
            }),
            e.url.trim(),
            e,
          ),
        ];
      }
      const orig = existing.find((ex) => ex.id === e.id);
      if (orig && !this.integrationChanged({ url: e.url, key }, orig, providerType)) {
        return [];
      }
      return [
        wrap(
          this.projectIntegrationService.update(projectId, e.id, {
            boardUrl: e.url.trim(),
            // Immer mitsenden, beim Leeren als null: mit `undefined` fiel das Feld aus
            // dem Payload und das Backend konnte das Kennzeichen nicht leeren (NEX-971).
            ...(key !== undefined ? { projectKey: key || null } : {}),
          }),
          e.url.trim(),
          e,
        ),
      ];
    });

    const deletes: Observable<IntegrationOpResult>[] = existing
      .filter((ex) => !filled.find((e) => e.id === ex.id))
      .map((ex) => wrap(this.projectIntegrationService.delete(projectId, ex.id), ex.boardUrl, null));

    return [...upserts, ...deletes];
  }

  private integrationChanged(
    entry: { url: string; key?: string },
    original: ProjectIntegration,
    providerType: ProviderType,
  ): boolean {
    // Trailing-Slash normalisieren: das Backend strippt ihn beim Persistieren, die UI nicht.
    const entryUrl = this.stripTrailingSlash(entry.url.trim());
    // Redmine: Kennzeichen wird direkt gespeichert (nicht aus der URL abgeleitet) —
    // daher gegen das rohe DTO vergleichen, nicht via Jira-Ableitung. Leeres Feld und
    // fehlendes Kennzeichen sind dabei derselbe Zustand, sonst meldete ein nie
    // gefuelltes Feld beim reinen Member-Save eine Aenderung (NEX-971).
    if (providerType === 'REDMINE') {
      if (entryUrl !== this.stripTrailingSlash((original.boardUrl ?? '').trim())) return true;
      if (entry.key === undefined) return false;
      return (entry.key || '').toUpperCase() !== (original.projectKey ?? '').toUpperCase();
    }
    // JIRA/GitHub: gegen den REKONSTRUIERTEN Ladezustand vergleichen, nicht gegen das
    // rohe DTO — beim Laden wird der JIRA-Key aus der URL abgeleitet, wenn das Backend
    // keinen speichert. Ein Vergleich gegen das leere projectKey meldete sonst "geändert"
    // und feuerte beim reinen Member-Save einen 400-auslösenden Update-Call (NEX-605).
    const loaded = jiraBoardFromIntegration(original);
    const loadedUrl = this.stripTrailingSlash(loaded.url.trim());
    if (entryUrl !== loadedUrl) return true;
    // key nur vergleichen, wenn der Eintrag überhaupt einen Key führt (JIRA hat einen, GitHub nicht).
    // Case-insensitiv, da geladener wie eingegebener JIRA-Key uppercased werden.
    if (entry.key === undefined) return false;
    return (entry.key || '').toUpperCase() !== loaded.key.toUpperCase();
  }

  private toBackendRoles(role: TeamMember['role']): ProjectRole[] {
    switch (role) {
      case 'lead':
        return ['PROJECT_ADMIN'];
      case 'tester':
        return ['PROJECT_WRITER'];
      default:
        return ['PROJECT_READER'];
    }
  }

  private fromBackendRole(roles: ProjectRole[]): TeamMember['role'] {
    switch (true) {
      case roles.includes('PROJECT_ADMIN'):
        return 'lead';
      case roles.includes('PROJECT_WRITER'):
        return 'tester';
      default:
        return 'observer';
    }
  }

  private toDateStr(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private parseDate(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
}
